export const PAYMENT_METHODS_NAMES = {
  ecopayz: 'EcoPayz',
  sberbank: 'Сбербанк',
  yoomoney: 'ЮMoney',
  uah_card: 'Карта UAH',
  rub_card: 'Карта RUB',
  megaphone: 'Мегафон',
  tether: 'Tether',
  skinpay: 'SkinPay',
  qiwi: 'Qiwi',
  mts: 'МТС',
  ethereum: 'Ethereum',
  card: 'Карта',
  bitcoin: 'Bitcoin',
  beeline: 'Билайн',
  alipay: 'Alipay',
  alfabank: 'Альфа-Банк',
  advcash: 'AdvCash',
  balance: 'Баланс',
  payok: 'PayOK',
  lava: 'СБП',
  gm_api_card: 'GameMoney',
  gm_api_sbp: 'GameMoney СБП',
  cardlink: 'Карты (РФ)',
  cardlink_sbp: 'СБП',
  cardlink_copy: 'Карты (РБ)',
  lava_new_mir_card: 'Карты (МИР)',
  lava_new_card: 'Карты (РФ)',
  lava_new_sbp: 'СБП',
  overpay: 'Карты (РФ)',
  overpay_sbp: 'СБП',
  cardlink_copy_2: 'Карты (Казахстан) ',
  platega_sbp: 'СБП',
  platega_card: 'Карты (РФ)'
}

// Platega: payment_type из списка SBP / CARD, иначе бэк отвечает 403.
// SBERPAY отдельным методом не выносим — у Platega он внутри СБП.
export const PLATEGA_PAYMENT_TYPES = {
  platega_sbp: 'SBP',
  platega_card: 'CARD'
}

/**
 * Получить название платежного метода по ключу
 * @param {string} key - Ключ платежного метода
 * @returns {string} Название платежного метода или сам ключ, если не найден
 */
export const getPaymentMethodName = key => {
  return PAYMENT_METHODS_NAMES[key] || key
}

/**
 * Создание платежа с обработкой различных методов оплаты
 * @param {Object} paymentMethod - Объект с информацией о методе оплаты
 * @param {string} paymentMethod.name - Название метода (например, 'cardlink_sbp', 'lava_card')
 * @param {string} paymentMethod.method - Метод платежа (например, 'SBP', 'card')
 * @param {string} orderId - ID заказа
 * @param {string} userId - ID пользователя
 * @param {number} amount - Сумма платежа
 * @param {string} email - Email (опционально)
 * @param {string} currency - Валюта (например, 'rub', 'usd')
 * @returns {Promise<{id: string, redirect_uri: string}|null>} Данные платежа с redirect_uri
 */
export const makePayment = async (paymentMethod, orderId, userId, amount, email = '', currency = 'rub') => {
  const { createPayment } = await import('./orderApi')

  let paymentType = ''
  let paymentSystem = paymentMethod?.name

  // Обработка различных методов оплаты
  if (paymentMethod.name === 'free-kassa' || paymentMethod.name === 'enot') {
    paymentType = null
    paymentSystem = paymentMethod.name
  }

  if (paymentMethod.name === 'rub_card') {
    paymentType = null
    paymentSystem = 'freekassa'
  }

  if (paymentMethod.name === 'gm_api') {
    paymentType = paymentMethod?.type || ''
    paymentSystem = 'gm'
  }

  if (paymentMethod.name === 'lava_card') {
    paymentSystem = 'lava'
    paymentType = 'card'
  }

  if (paymentMethod.name === 'lava_sbp') {
    paymentSystem = 'lava'
    paymentType = 'sbp'
  }

  if (paymentMethod.name === 'antilopay_sberpay') {
    paymentSystem = 'antilopay'
    paymentType = 'antilopayGateway'
  }

  if (paymentMethod.name.includes('lava_new')) {
    paymentSystem = 'lava'
    paymentType = paymentMethod.method || ''
  }

  if (paymentMethod.name.includes('cardlink')) {
    paymentSystem = 'cardlink'
    if (paymentMethod.name === 'cardlink_sbp') {
      paymentType = 'SBP'
    } else if (paymentMethod.name === 'cardlink') {
      paymentType = 'BANK_CARD'
    } else if (paymentMethod.name === 'cardlink_sberpay') {
      paymentType = 'SBERPAY'
    } else {
      paymentType = paymentMethod.method || ''
    }
  }

  if (PLATEGA_PAYMENT_TYPES[paymentMethod.name]) {
    paymentSystem = 'platega'
    paymentType = PLATEGA_PAYMENT_TYPES[paymentMethod.name]
  }

  if (paymentMethod.name.includes('overpay')) {
    paymentSystem = 'overpay'
    if (paymentMethod.name === 'overpay_sbp') {
      paymentType = 'sbp'
    } else {
      paymentType = 'credit_card'
    }
  }

  // Создание платежа
  const result = await createPayment(orderId, userId, amount, paymentType, paymentSystem, email, currency)

  if (!result) {
    return null
  }

  // Извлекаем URL из различных форматов ответа
  let redirectUri = null

  // Прямой redirect_uri
  if (result.redirect_uri) {
    redirectUri = result.redirect_uri
  } else if (result.cardlink?.uri) {
    redirectUri = result.cardlink.uri
  } else if (result.platega?.uri) {
    redirectUri = result.platega.uri
  } else if (result.overpay?.uri) {
    redirectUri = result.overpay.uri
  } else if (result.overpay && typeof result.overpay === 'string') {
    // overpay может вернуть строку напрямую
    redirectUri = result.overpay
  } else if (result.lava?.uri) {
    redirectUri = result.lava.uri
  } else if (result.antilopay?.uri) {
    redirectUri = result.antilopay.uri
  } else if (result.gm?.uri) {
    redirectUri = result.gm.uri
  } else if (result.gram?.uri) {
    redirectUri = result.gram.uri
  } else if (result.pay_ok?.uri) {
    redirectUri = result.pay_ok.uri
  } else if (result.new_fk?.uri) {
    redirectUri = result.new_fk.uri
  } else if (result.payUrl || result.pay_url || result.url) {
    redirectUri = result.payUrl || result.pay_url || result.url
  }

  if (redirectUri) {
    return {
      id: result.id,
      redirect_uri: redirectUri
    }
  }

  return null
}
